import { useState, useEffect, useCallback, useMemo } from "react";

const API_BASE = "https://api.weatherdatamanager.gwc.dm-airtech.com";

const C = {
  bg:"#0f1117",surface:"#181c25",surfaceAlt:"#1e2330",border:"#2a3045",
  text:"#e8ecf4",textSub:"#7a8499",textMuted:"#4a5268",
  accent:"#4f8ef7",accentDim:"#1e3a6e",success:"#3ecf8e",successDim:"#0d3d28",
  warn:"#f5a623",danger:"#f05252",
};

const apiJson = async (path) => {
  const r = await fetch(`${API_BASE}${path}`, { headers:{"Content-Type":"application/json"} });
  if(!r.ok) throw new Error(r.statusText);
  return r.json();
};

const apiPost = async (path, body) => {
  const r = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || r.statusText);
  return data;
};

const apiDelete = async (path) => {
  const r = await fetch(`${API_BASE}${path}`, { method: "DELETE" });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.detail || r.statusText);
  return data;
};

const pill = (label, color=C.accent, bg=C.accentDim) => (
  <span style={{ fontSize:10, fontWeight:600, padding:"2px 7px", borderRadius:20, background:bg, color, letterSpacing:0.4 }}>{label}</span>
);
const Card = ({ children, style={} }) => (
  <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"1rem 1.125rem", ...style }}>{children}</div>
);
const Spinner = () => (
  <span style={{ display:"inline-block", width:14, height:14, border:`2px solid ${C.border}`, borderTopColor:C.accent, borderRadius:"50%", animation:"spin 0.7s linear infinite" }} />
);
const Label = ({ children }) => (
  <label style={{ fontSize:11, color:C.textSub, display:"block", marginBottom:4 }}>{children}</label>
);
const Err = ({ msg }) => msg ? (
  <div style={{ background:C.dangerDim||"#3d1010", border:`1px solid ${C.danger}`, borderRadius:8, padding:"8px 12px", fontSize:12, color:C.danger, marginBottom:10 }}>{msg}</div>
) : null;
const fmtVal = (v, decimals=1, suffix="") => v!=null?`${parseFloat(v).toFixed(decimals)}${suffix}`:"—";

const inputStyle = { width:"100%", background:C.surfaceAlt, border:`1px solid ${C.border}`, color:C.text, borderRadius:6, padding:"7px 10px", fontSize:13 };

// How stale a device's last weather_readings write can be before we call it
// "inactive" rather than "active" — 15 min comfortably covers the slowest
// known cadence (10 min) plus network/poll jitter, without being so loose
// that a genuinely dead source still shows green for hours.
const FRESHNESS_THRESHOLD_MIN = 15;

// Brands managed via POST /devices/register + DELETE /devices/{id} —
// always-on, DB-driven pollers (ecowitt_poller.py, tempest_poller.py)
// that reload their device list every cycle, as opposed to liveSources
// below (fetchers/-based, spawned per-station via /pollers/start).
const REGISTRABLE_BRANDS = [
  {
    id: "ecowitt",
    label: "Ecowitt",
    identifierLabel: "MAC address",
    identifierPlaceholder: "84:1F:E8:35:F9:9F",
    needsDeviceId: true,
    deviceIdHelp: "Must be Ecowitt's OWN device_id for this station (e.g. \"D707\") — " +
                  "not the MAC. Call Ecowitt's API once with the new MAC first if you " +
                  "don't already know it.",
  },
  {
    id: "tempest",
    label: "Tempest",
    identifierLabel: "Station ID",
    identifierPlaceholder: "197211",
    needsDeviceId: false,
    deviceIdHelp: "Auto-derived as TEMPEST_<station_id> — leave blank unless you need something else.",
  },
];

export default function MonitorTab() {
  const [health,setHealth]=useState(null);
  const [readings,setReadings]=useState([]);
  const [sources,setSources]=useState([]);
  const [pollers,setPollers]=useState([]);           // /inference/pollers (ML pipeline)
  const [genericPollers,setGenericPollers]=useState([]); // /pollers (general-purpose)
  const [registeredDevices,setRegisteredDevices]=useState([]); // /devices (ecowitt + tempest)
  const [inferenceData,setInferenceData]=useState([]);
  const [deviceSummary,setDeviceSummary]=useState([]);
  const [loading,setLoading]=useState(true);
  const [lastRefresh,setLastRefresh]=useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [h,r,s,pol,inf,gp,dev] = await Promise.all([
        apiJson("/health"),
        apiJson("/readings?limit=50"),
        apiJson("/sources"),
        apiJson("/inference/pollers"),
        apiJson("/inference/data?hours=24"),
        apiJson("/pollers"),
        apiJson("/devices"),
      ]);
      setHealth(h);
      setReadings(r.data||[]);
      setSources(s.sources||[]);
      setPollers(pol.pollers||[]);
      setInferenceData(inf.data||[]);
      setGenericPollers(gp.pollers||[]);
      setRegisteredDevices(dev.devices||[]);
      const byDevice={};
      for(const row of (r.data||[])){
        if(!byDevice[row.device_id]) byDevice[row.device_id]={device_id:row.device_id,source:row.source,rows:0,latest:row};
        byDevice[row.device_id].rows++;
      }
      setDeviceSummary(Object.values(byDevice));
      setLastRefresh(new Date());
    } catch{}
    finally{setLoading(false);}
  },[]);

  useEffect(()=>{refresh();},[]);

  const StatusDot = ({up}) => (
    <span style={{ display:"inline-block", width:8, height:8, borderRadius:"50%", background:up?C.success:C.danger, marginRight:6 }} />
  );

  // ── Sources that actually support live polling — the only valid choices
  // for the generic "Add poller" form, since /pollers/start ultimately
  // calls /live/latest under the hood, which only knows these sources.
  // Deliberately excludes ecowitt/tempest even if either shows up in
  // /sources — those go through /devices/register instead (see
  // REGISTRABLE_BRANDS above), not the subprocess-per-station model this
  // list drives. (tempest is intentionally NOT wired into FETCHERS'
  // generic-poller path server-side, to avoid a second process writing
  // the same device_id tempest_poller.py already owns — see pollers/README.md.)
  const liveSources = useMemo(
    () => sources.filter(s => (s.data_types || []).includes("live") && !["ecowitt","tempest"].includes(s.id)),
    [sources]
  );

  // ── Unified "what's actually collecting" view ─────────────────────────────
  // Four separate registries each answer a narrower question:
  //   - /pollers only knows about pollers THIS dashboard started (JSON registry)
  //   - /inference/pollers only knows about ML-pipeline pollers (separate registry)
  //   - /devices knows about Ecowitt/Tempest devices registered via the DB —
  //     these ARE manageable from here (register/deactivate), just not via
  //     a spawned subprocess like the other two.
  //   - deviceSummary (from /readings, i.e. weather_readings itself) is the
  //     one signal that works for ANY device regardless of how it got
  //     there, so it's the base of truth, with the three registries
  //     overlaid on top to add management info where an entry exists.
  const unifiedDevices = useMemo(() => {
    const map = {};

    for (const d of deviceSummary) {
      map[d.device_id] = {
        device_id: d.device_id,
        source: d.source,
        lastSeen: d.latest?.polled_at,
        management: "external",   // overridden below if a registry entry exists
        interval: null,
        running: null,
        stoppable: false,
        stopAction: null,
      };
    }

    for (const d of registeredDevices) {
      map[d.device_id] = {
        ...(map[d.device_id] || { device_id: d.device_id, source: d.source }),
        lastSeen: map[d.device_id]?.lastSeen || d.last_seen,
        management: `${d.brand} (registered device)`,
        interval: d.poll_interval_sec,
        running: !!d.is_active,
        stoppable: true,
        stopAction: { type: "device", id: d.device_id },
      };
    }

    for (const p of genericPollers) {
      map[p.device_id] = {
        ...(map[p.device_id] || { device_id: p.device_id, source: p.source }),
        lastSeen: map[p.device_id]?.lastSeen || p.last_seen,
        management: "dashboard poller",
        interval: p.interval_seconds,
        running: p.running,
        stoppable: true,
        stopAction: { type: "poller", id: p.device_id },
      };
    }

    for (const p of pollers) {
      const key = p.device_id;
      const already = map[key];
      map[key] = {
        ...(already || { device_id: key, source: p.source }),
        lastSeen: already?.lastSeen || p.last_seen,
        management: already?.management === "dashboard poller"
          ? "dashboard poller + inference"
          : (already?.management || "inference poller"),
        interval: already?.interval || p.interval_seconds,
        running: already?.running ?? p.running,
        // /inference/pollers doesn't expose a stop control in this dashboard
        // today — deliberately not overriding stoppable/stopAction here if
        // an entry already exists from one of the other registries above.
        stoppable: already?.stoppable || false,
        stopAction: already?.stopAction || null,
      };
    }

    const now = Date.now();
    return Object.values(map)
      .map(d => {
        const lastSeenMs = d.lastSeen ? new Date(d.lastSeen.endsWith("Z") ? d.lastSeen : d.lastSeen + "Z").getTime() : null;
        const freshMinutes = lastSeenMs != null ? (now - lastSeenMs) / 60000 : null;
        return { ...d, freshMinutes, isActive: freshMinutes != null && freshMinutes < FRESHNESS_THRESHOLD_MIN };
      })
      .sort((a, b) => (a.freshMinutes ?? Infinity) - (b.freshMinutes ?? Infinity));
  }, [deviceSummary, genericPollers, pollers, registeredDevices]);

  return (
    <div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
        <p style={{ fontSize:13, color:C.textSub }}>{lastRefresh?`Last updated: ${lastRefresh.toLocaleTimeString()}`:"Loading…"}</p>
        <button onClick={refresh} disabled={loading}>
          {loading?<><Spinner /> Refreshing…</>:"↻ Refresh"}
        </button>
      </div>

      {/* API health */}
      {health&&(
        <Card style={{ marginBottom:16 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
            <p style={{ fontSize:13, fontWeight:600 }}>API health</p>
            {pill("v"+health.version,C.success,C.successDim)}
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {Object.entries(health.sensors||{}).map(([name,status])=>(
              <div key={name} style={{ background:C.surfaceAlt, borderRadius:8, padding:"8px 12px", fontSize:12 }}>
                <StatusDot up={status==="up"} />
                <span style={{ fontWeight:500 }}>{name}</span>
                <span style={{ color:status==="up"?C.success:C.danger, marginLeft:8 }}>{status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Add a poller / register a device ───────────────────────────────── */}
      <AddPollerCard liveSources={liveSources} onStarted={refresh} />

      {/* ── Unified view: everything actually collecting right now ───────── */}
      {unifiedDevices.length > 0 && (
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>All collections</p>
          <p style={{ fontSize:11, color:C.textSub, marginBottom:12 }}>
            Status is based on how recently each device actually wrote to weather_readings
            (within {FRESHNESS_THRESHOLD_MIN} min) — not just whether a poller process this
            dashboard started is still alive. Ecowitt/Tempest devices are always-on services
            managed via device registration rather than a spawned subprocess, but ARE
            deactivatable from here.
          </p>
          <div style={{ overflowX:"auto" }}>
            <table>
              <thead><tr><th>Device</th><th>Source</th><th>Managed by</th><th>Interval</th><th>Last seen</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {unifiedDevices.map(d=>(
                  <tr key={d.device_id}>
                    <td style={{ fontWeight:500, fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>{d.device_id}</td>
                    <td>{pill((d.source||"—").toUpperCase())}</td>
                    <td style={{ fontSize:11, color:C.textSub }}>{d.management}</td>
                    <td style={{ color:C.textSub }}>{d.interval ? `${d.interval}s` : "—"}</td>
                    <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>
                      {d.lastSeen ? d.lastSeen.slice(0,16).replace("T"," ") : "—"}
                    </td>
                    <td>{d.isActive ? <span style={{ color:C.success }}>● active</span> : <span style={{ color:C.danger }}>● inactive</span>}</td>
                    <td>{d.stoppable && <StopButton action={d.stopAction} onStopped={refresh} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Registered devices (Ecowitt/Tempest) — detail view ───────────── */}
      {registeredDevices.length>0&&(
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Registered devices (Ecowitt / Tempest)</p>
          <div style={{ overflowX:"auto" }}>
            <table>
              <thead><tr><th>Device</th><th>Brand</th><th>Identifier</th><th>Station</th><th>Last seen</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {registeredDevices.map(d=>(
                  <tr key={d.device_id}>
                    <td style={{ fontWeight:500, fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>{d.device_id}</td>
                    <td>{pill(d.brand.toUpperCase())}</td>
                    <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>{d.api_identifier}</td>
                    <td style={{ fontSize:11, color:C.textSub }}>{d.station_name || "—"}</td>
                    <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>
                      {d.last_seen ? d.last_seen.slice(0,16).replace("T"," ") : "—"}
                    </td>
                    <td>{d.is_active ? <span style={{ color:C.success }}>● active</span> : <span style={{ color:C.textMuted }}>○ inactive</span>}</td>
                    <td>
                      {d.is_active && (
                        <StopButton action={{ type: "device", id: d.device_id }} onStopped={refresh} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── General-purpose pollers (from /pollers) — detail view ─────────── */}
      {genericPollers.length>0&&(
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Dashboard-started pollers (weather_readings)</p>
          <div style={{ overflowX:"auto" }}>
            <table>
              <thead><tr><th>Device</th><th>Source</th><th>Station</th><th>Topic</th><th>Interval</th><th>Rows</th><th>Last seen</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {genericPollers.map(p=>(
                  <PollerRow key={p.device_id} poller={p} onStopped={refresh} />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Device cards */}
      {deviceSummary.length>0&&(
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:4 }}>Stored sensor data (weather_readings)</p>
          <p style={{ fontSize:11, color:C.textSub, marginBottom:12 }}>Live conditions from DB — only devices actively writing to weather_readings</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(220px,1fr))", gap:10 }}>
            {deviceSummary.map(d=>{
              const r=d.latest;
              return (
                <div key={d.device_id} style={{ background:C.surfaceAlt, borderRadius:10, padding:"12px 14px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                    <span style={{ fontWeight:600, fontSize:13 }}>{d.device_id}</span>
                    {pill(d.source||"—")}
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
                    {[
                      ["Temp",fmtVal(r.outdoor_temperature,1,"°C")],
                      ["Humidity",fmtVal(r.outdoor_humidity,0,"%")],
                      ["Wind",fmtVal(r.wind_speed,1," m/s")],
                      ["Solar",fmtVal(r.solar,0," W/m²")],
                      ["Pressure",fmtVal(r.pressure_relative,0," hPa")],
                      ["Rain 1h",fmtVal(r.rain_1h,1," mm")],
                    ].map(([label,value])=>(
                      <div key={label}>
                        <p style={{ fontSize:10, color:C.textMuted }}>{label}</p>
                        <p style={{ fontSize:13, fontWeight:500, fontFamily:"JetBrains Mono,monospace" }}>{value}</p>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontSize:10, color:C.textMuted, marginTop:10 }}>{r.polled_at?.slice(0,16).replace("T"," ")}</p>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Inference pollers */}
      {pollers.length>0&&(
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Inference pollers (inference_input)</p>
          <table>
            <thead><tr><th>Device</th><th>Source</th><th>Interval</th><th>Rows (24h)</th><th>Last seen</th><th>Status</th></tr></thead>
            <tbody>
              {pollers.map(p=>(
                <tr key={p.poller_id}>
                  <td style={{ fontWeight:500, fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>{p.device_id}</td>
                  <td>{pill(p.source.toUpperCase())}</td>
                  <td style={{ color:C.textSub }}>{p.interval_seconds}s</td>
                  <td style={{ color:p.row_count>0?C.success:C.textSub, fontWeight:500 }}>{p.row_count}</td>
                  <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>{p.last_seen?p.last_seen.slice(0,16).replace("T"," "):"—"}</td>
                  <td>{p.running?<span style={{ color:C.success }}>● running</span>:<span style={{ color:C.danger }}>● stopped</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Inference data summary */}
      {inferenceData.length>0&&(
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Inference input data (last 24h)</p>
          <table>
            <thead><tr><th>Device</th><th>Source</th><th>Rows</th><th>Completeness</th><th>From</th><th>To</th><th>Ready</th></tr></thead>
            <tbody>
              {inferenceData.map(d=>(
                <tr key={d.device_id}>
                  <td style={{ fontWeight:500, fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>{d.device_id}</td>
                  <td>{pill(d.source?.toUpperCase()||"—")}</td>
                  <td style={{ color:C.success, fontWeight:500 }}>{d.count}</td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:60, height:4, background:C.border, borderRadius:2 }}>
                        <div style={{ width:`${d.completeness_pct||0}%`, height:"100%", background:d.ready_for_export?C.success:C.accent, borderRadius:2 }} />
                      </div>
                      <span style={{ fontSize:11, color:C.textSub }}>{d.completeness_pct}%</span>
                    </div>
                  </td>
                  <td style={{ fontSize:11, color:C.textSub }}>{d.earliest?.slice(0,16).replace("T"," ")}</td>
                  <td style={{ fontSize:11, color:C.textSub }}>{d.latest?.slice(0,16).replace("T"," ")}</td>
                  <td>{d.ready_for_export?<span style={{ color:C.success }}>✓ yes</span>:<span style={{ color:C.textMuted }}>not yet</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Recent readings */}
      {readings.length>0&&(
        <Card style={{ marginBottom:16 }}>
          <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Recent readings log</p>
          <div style={{ overflowX:"auto" }}>
            <table>
              <thead><tr><th>Device</th><th>Source</th><th>Time</th><th>Temp</th><th>Humidity</th><th>Wind</th><th>Pressure</th><th>Solar</th></tr></thead>
              <tbody>
                {readings.slice(0,10).map((r,i)=>(
                  <tr key={i}>
                    <td style={{ fontWeight:500 }}>{r.device_id}</td>
                    <td style={{ color:C.textSub, fontSize:11 }}>{r.source}</td>
                    <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>{r.polled_at?.slice(0,16).replace("T"," ")}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace" }}>{fmtVal(r.outdoor_temperature,1,"°")}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace" }}>{fmtVal(r.outdoor_humidity,0,"%")}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace" }}>{fmtVal(r.wind_speed,1,"m/s")}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace" }}>{fmtVal(r.pressure_relative,0,"hPa")}</td>
                    <td style={{ fontFamily:"JetBrains Mono,monospace" }}>{fmtVal(r.solar,0,"W")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Sources */}
      <Card>
        <p style={{ fontSize:13, fontWeight:600, marginBottom:10 }}>Available data sources ({sources.length + REGISTRABLE_BRANDS.length})</p>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px,1fr))", gap:8 }}>
          {sources.map(s=>(
            <div key={s.id} style={{ background:C.surfaceAlt, borderRadius:8, padding:"8px 12px" }}>
              <p style={{ fontSize:12, fontWeight:600, marginBottom:2 }}>{s.id.toUpperCase()}</p>
              <p style={{ fontSize:10, color:C.textSub, marginBottom:4 }}>{s.coverage}</p>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {(s.data_types||[]).map(t=>pill(t,t==="live"?C.success:C.accent,t==="live"?C.successDim:C.accentDim))}
              </div>
            </div>
          ))}
          {/* Ecowitt/Tempest aren't in GET /sources — they're intentionally
              not in FETCHERS (see pollers/README.md), so they'd otherwise be
              invisible here even though both are real, working sources
              managed via device registration instead of /pollers/start. */}
          {REGISTRABLE_BRANDS.map(b=>(
            <div key={b.id} style={{ background:C.surfaceAlt, borderRadius:8, padding:"8px 12px" }}>
              <p style={{ fontSize:12, fontWeight:600, marginBottom:2 }}>{b.label.toUpperCase()}</p>
              <p style={{ fontSize:10, color:C.textSub, marginBottom:4 }}>Fixed station(s), always-on</p>
              <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
                {pill("live",C.success,C.successDim)}
                {pill("device-managed",C.accent,C.accentDim)}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ── Add poller / register device card ──────────────────────────────────────────
// Two modes, toggled at the top: "Generic live source" (existing
// /pollers/start flow, spawns a subprocess per station) and
// "Ecowitt / Tempest device" (new /devices/register flow, no subprocess —
// the always-on brand poller picks it up within one poll cycle).
function AddPollerCard({ liveSources, onStarted }) {
  const [mode, setMode] = useState("generic"); // "generic" | "device"

  return (
    <Card style={{ marginBottom: 16 }}>
      <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>➕ Add a poller</p>
      <p style={{ fontSize: 11, color: C.textSub, marginBottom: 12 }}>
        Start collecting from any live-capable source, any time — for a one-off
        station or a newly bought company weather station. New sources not
        listed here need a fetcher added server-side first.
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        <button
          onClick={() => setMode("generic")}
          className={mode === "generic" ? "primary" : ""}
          style={{ flex: 1, ...(mode === "generic" ? { background: C.accent, borderColor: C.accent, color: "#fff" } : {}) }}
        >
          Generic live source
        </button>
        <button
          onClick={() => setMode("device")}
          className={mode === "device" ? "primary" : ""}
          style={{ flex: 1, ...(mode === "device" ? { background: C.accent, borderColor: C.accent, color: "#fff" } : {}) }}
        >
          Ecowitt / Tempest device
        </button>
      </div>

      {mode === "generic"
        ? <GenericPollerForm liveSources={liveSources} onStarted={onStarted} />
        : <RegisterDeviceForm onRegistered={onStarted} />}
    </Card>
  );
}

function GenericPollerForm({ liveSources, onStarted }) {
  const [source, setSource] = useState("");
  const [stationId, setStationId] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [brand, setBrand] = useState("");
  const [topic, setTopic] = useState("");
  const [intervalSeconds, setIntervalSeconds] = useState(600);
  const [stationName, setStationName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // {ok, data|error}
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [justStarted, setJustStarted] = useState(null);

  const selectedSource = liveSources.find(s => s.id === source);

  // Suggested defaults — only applied when the user hasn't typed their own
  // value yet, so overriding any one field never gets clobbered on the
  // next keystroke elsewhere.
  const suggestedDeviceId = source && stationId ? `${source.toUpperCase()}_${stationId}` : "";
  const suggestedTopic    = source && stationId ? `sensors/custom/${source}_${stationId}` : "";

  function reset() {
    setSource(""); setStationId(""); setDeviceId(""); setBrand("");
    setTopic(""); setIntervalSeconds(600); setStationName(""); setLat(""); setLon("");
    setTestResult(null); setError("");
  }

  async function testFetch() {
    if (!source || !stationId) { setError("Pick a source and enter a station ID first."); return; }
    setTesting(true); setError(""); setTestResult(null);
    try {
      const r = await apiJson(`/live/latest?source=${encodeURIComponent(source)}&station_id=${encodeURIComponent(stationId)}`);
      setTestResult(r);
      if (r.ok && r.data) {
        if (!stationName && r.data.station_name) setStationName(r.data.station_name);
        if (!lat && r.data.lat != null) setLat(String(r.data.lat));
        if (!lon && r.data.lon != null) setLon(String(r.data.lon));
      }
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally {
      setTesting(false);
    }
  }

  async function startPoller() {
    const finalDeviceId = deviceId || suggestedDeviceId;
    const finalTopic    = topic || suggestedTopic;
    const finalBrand    = brand || source;

    if (!source || !stationId || !finalDeviceId || !finalTopic || !finalBrand) {
      setError("Source, station ID, device ID, topic and brand are all required.");
      return;
    }

    setStarting(true); setError(""); setJustStarted(null);
    try {
      const r = await apiPost("/pollers/start", {
        source,
        station_id: stationId,
        topic: finalTopic,
        device_id: finalDeviceId,
        brand: finalBrand,
        interval_seconds: Number(intervalSeconds) || 600,
        station_name: stationName || null,
        lat: lat !== "" ? Number(lat) : null,
        lon: lon !== "" ? Number(lon) : null,
      });
      setJustStarted(r.poller);
      onStarted?.();
      reset();
    } catch (e) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  }

  return (
    <div>
      <Err msg={error} />

      {justStarted && (
        <div style={{ background: C.successDim, border: `1px solid ${C.success}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
          ✅ Started poller for <strong>{justStarted.device_id}</strong> (pid {justStarted.pid}) — it'll show up in "All collections" below.
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <Label>Source</Label>
          <select value={source} onChange={e => setSource(e.target.value)} style={inputStyle}>
            <option value="">— select a live source —</option>
            {liveSources.map(s => (
              <option key={s.id} value={s.id}>{s.id.toUpperCase()} — {s.best_for || s.coverage}</option>
            ))}
          </select>
        </div>
        <div>
          <Label>Station ID {selectedSource?.station_id_format ? `(format: ${selectedSource.station_id_format})` : ""}</Label>
          <input
            value={stationId}
            onChange={e => setStationId(e.target.value)}
            placeholder={selectedSource?.example_station || ""}
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
        <button onClick={testFetch} disabled={testing || !source || !stationId}>
          {testing ? <><Spinner /> Testing…</> : "▶ Test fetch"}
        </button>
        {testResult && (
          testResult.ok ? (
            <span style={{ fontSize: 12, color: C.success }}>
              ✓ {testResult.data?.station_name || stationId} — {fmtVal(testResult.data?.data?.temperature, 1, "°C")},
              wind {fmtVal(testResult.data?.data?.wind_speed, 1, " m/s")}, as of {testResult.data?.valid_at?.slice(0, 16).replace("T", " ")}
            </span>
          ) : (
            <span style={{ fontSize: 12, color: C.danger }}>✗ {testResult.error}</span>
          )
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 10 }}>
        <div>
          <Label>Device ID</Label>
          <input value={deviceId} onChange={e => setDeviceId(e.target.value)}
            placeholder={suggestedDeviceId || "e.g. DWD_01975"} style={inputStyle} />
        </div>
        <div>
          <Label>Brand</Label>
          <input value={brand} onChange={e => setBrand(e.target.value)}
            placeholder={source || "e.g. dwd"} style={inputStyle} />
        </div>
        <div>
          <Label>Interval (seconds)</Label>
          <input type="number" min={30} value={intervalSeconds}
            onChange={e => setIntervalSeconds(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <div style={{ marginBottom: 10 }}>
        <Label>MQTT topic</Label>
        <input value={topic} onChange={e => setTopic(e.target.value)}
          placeholder={suggestedTopic || "sensors/custom/source_stationid"} style={inputStyle} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
        <div>
          <Label>Station name (optional)</Label>
          <input value={stationName} onChange={e => setStationName(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <Label>Lat (optional)</Label>
          <input value={lat} onChange={e => setLat(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <Label>Lon (optional)</Label>
          <input value={lon} onChange={e => setLon(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <button className="primary" onClick={startPoller} disabled={starting || !source || !stationId}
        style={{ background: C.accent, borderColor: C.accent, color: "#fff", width: "100%" }}>
        {starting ? <><Spinner /> Starting…</> : "🚀 Start poller"}
      </button>
    </div>
  );
}

// ── Register an Ecowitt/Tempest device via POST /devices/register ──────────
// No subprocess spawned, no restart needed — ecowitt_poller.py /
// tempest_poller.py reload their device list from the sensors table every
// poll cycle (≤60s), so this takes effect automatically.
function RegisterDeviceForm({ onRegistered }) {
  const [brandId, setBrandId] = useState(REGISTRABLE_BRANDS[0].id);
  const [apiIdentifier, setApiIdentifier] = useState("");
  const [deviceId, setDeviceId] = useState("");
  const [stationName, setStationName] = useState("");
  const [lat, setLat] = useState("");
  const [lon, setLon] = useState("");

  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState("");
  const [justRegistered, setJustRegistered] = useState(null);

  const brand = REGISTRABLE_BRANDS.find(b => b.id === brandId);

  function reset() {
    setApiIdentifier(""); setDeviceId(""); setStationName(""); setLat(""); setLon("");
    setError("");
  }

  async function register() {
    if (!apiIdentifier) {
      setError(`${brand.identifierLabel} is required.`);
      return;
    }
    if (brand.needsDeviceId && !deviceId) {
      setError(`Device ID is required for ${brand.label} — see the note below the field.`);
      return;
    }

    setRegistering(true); setError(""); setJustRegistered(null);
    try {
      const r = await apiPost("/devices/register", {
        brand: brand.id,
        api_identifier: apiIdentifier,
        device_id: deviceId || null,
        station_name: stationName || null,
        lat: lat !== "" ? Number(lat) : null,
        lon: lon !== "" ? Number(lon) : null,
      });
      setJustRegistered(r);
      onRegistered?.();
      reset();
    } catch (e) {
      setError(e.message);
    } finally {
      setRegistering(false);
    }
  }

  return (
    <div>
      <Err msg={error} />

      {justRegistered && (
        <div style={{ background: C.successDim, border: `1px solid ${C.success}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, marginBottom: 12 }}>
          ✅ Registered <strong>{justRegistered.device_id}</strong> — the {brand.label} poller
          picks it up within ~60s (no restart needed). It'll show up in
          "Registered devices" and "All collections" below once it starts publishing.
        </div>
      )}

      <div style={{ marginBottom: 12 }}>
        <Label>Brand</Label>
        <div style={{ display: "flex", gap: 8 }}>
          {REGISTRABLE_BRANDS.map(b => (
            <button
              key={b.id}
              onClick={() => { setBrandId(b.id); setError(""); }}
              style={{
                flex: 1,
                ...(brandId === b.id ? { background: C.accent, borderColor: C.accent, color: "#fff" } : {}),
              }}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: brand.needsDeviceId ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 4 }}>
        <div>
          <Label>{brand.identifierLabel}</Label>
          <input
            value={apiIdentifier}
            onChange={e => setApiIdentifier(e.target.value)}
            placeholder={brand.identifierPlaceholder}
            style={inputStyle}
          />
        </div>
        {brand.needsDeviceId && (
          <div>
            <Label>Device ID</Label>
            <input
              value={deviceId}
              onChange={e => setDeviceId(e.target.value)}
              placeholder="e.g. AB12"
              style={inputStyle}
            />
          </div>
        )}
      </div>
      <p style={{ fontSize: 10, color: C.textMuted, marginBottom: 14 }}>{brand.deviceIdHelp}</p>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
        <div>
          <Label>Station name (optional)</Label>
          <input value={stationName} onChange={e => setStationName(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <Label>Lat (optional)</Label>
          <input value={lat} onChange={e => setLat(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <Label>Lon (optional)</Label>
          <input value={lon} onChange={e => setLon(e.target.value)} style={inputStyle} />
        </div>
      </div>

      <p style={{ fontSize: 10, color: C.textMuted, marginBottom: 12 }}>
        This only registers which station to poll — account credentials
        (API keys / tokens) are configured separately on the server and
        aren't entered here. Registering only works if a {brand.label} account
        is already configured server-side.
      </p>

      <button className="primary" onClick={register} disabled={registering || !apiIdentifier}
        style={{ background: C.accent, borderColor: C.accent, color: "#fff", width: "100%" }}>
        {registering ? <><Spinner /> Registering…</> : `📡 Register ${brand.label} device`}
      </button>
    </div>
  );
}

// ── One row in the detailed generic-pollers table, with its own stop button ──
function PollerRow({ poller, onStopped }) {
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState("");

  async function stop() {
    setStopping(true); setError("");
    try {
      await apiDelete(`/pollers/${encodeURIComponent(poller.device_id)}`);
      onStopped?.();
    } catch (e) {
      setError(e.message);
      setStopping(false);
    }
  }

  return (
    <tr>
      <td style={{ fontWeight:500, fontFamily:"JetBrains Mono,monospace", fontSize:11 }}>{poller.device_id}</td>
      <td>{pill(poller.source?.toUpperCase() || "—")}</td>
      <td style={{ fontSize:11, color:C.textSub }}>{poller.station_id}</td>
      <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>{poller.topic}</td>
      <td style={{ color:C.textSub }}>{poller.interval_seconds}s</td>
      <td style={{ color:poller.row_count>0?C.success:C.textSub, fontWeight:500 }}>{poller.row_count}</td>
      <td style={{ fontSize:11, color:C.textSub, fontFamily:"JetBrains Mono,monospace" }}>
        {poller.last_seen ? poller.last_seen.slice(0,16).replace("T"," ") : "—"}
      </td>
      <td>{poller.running ? <span style={{ color:C.success }}>● running</span> : <span style={{ color:C.danger }}>● stopped</span>}</td>
      <td>
        <button onClick={stop} disabled={stopping} style={{ fontSize:11, padding:"3px 10px", color:C.danger, borderColor:C.danger }}>
          {stopping ? <Spinner /> : "Stop"}
        </button>
        {error && <p style={{ fontSize:10, color:C.danger, marginTop:4 }}>{error}</p>}
      </td>
    </tr>
  );
}

// ── Unified stop button — routes to DELETE /pollers/{id} or
// DELETE /devices/{id} depending on how the row is actually managed.
// A generic-poller device_id and a registered-device device_id never
// collide with each other's endpoint by construction, but the action
// object makes the distinction explicit rather than guessing from shape.
function StopButton({ action, onStopped }) {
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState("");

  async function stop() {
    if (!action) return;
    setStopping(true); setError("");
    try {
      const path = action.type === "device"
        ? `/devices/${encodeURIComponent(action.id)}`
        : `/pollers/${encodeURIComponent(action.id)}`;
      await apiDelete(path);
      onStopped?.();
    } catch (e) {
      setError(e.message);
      setStopping(false);
    }
  }

  return (
    <>
      <button onClick={stop} disabled={stopping} style={{ fontSize:11, padding:"3px 10px", color:C.danger, borderColor:C.danger }}>
        {stopping ? <Spinner /> : (action?.type === "device" ? "Deactivate" : "Stop")}
      </button>
      {error && <p style={{ fontSize:10, color:C.danger, marginTop:4 }}>{error}</p>}
    </>
  );
}